// components/productos/ProductoOpcionesModal.jsx
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { OpcionesProductoService } from '../../services/opcionesProductoService';

const fmt = (n) => `S/ ${parseFloat(n || 0).toFixed(2)}`;
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent';
const selectCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white';
const btnPrimary = 'flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors';

// ── Confirm inline ────────────────────────────────────────────────────────────
// Reemplaza window.confirm — muestra botones Confirmar/Cancelar en línea
function ConfirmInline({ mensaje, onConfirm, onCancel }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm">
      <span className="text-red-700 flex-1">{mensaje}</span>
      <button onClick={onConfirm}
        className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors">
        Eliminar
      </button>
      <button onClick={onCancel}
        className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-300 transition-colors">
        Cancelar
      </button>
    </div>
  );
}

// ── OpcionForm ────────────────────────────────────────────────────────────────
function OpcionForm({ idGrupo, opcion, onSaved, onCancel }) {
  const [nombre, setNombre]   = useState(opcion?.nombre ?? '');
  const [precio, setPrecio]   = useState(opcion?.precio_adicional ?? 0);
  const [imgPrev, setImgPrev] = useState(opcion?.imagen ? `data:image/jpeg;base64,${opcion.imagen}` : null);
  const [imgFile, setImgFile] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const imgRef = useRef();

  const guardar = async () => {
    if (!nombre.trim()) { setError('El nombre es requerido'); return; }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('id_grupo', idGrupo);
      fd.append('nombre', nombre.trim());
      fd.append('precio_adicional', precio);
      fd.append('disponible', true);
      fd.append('orden', opcion?.orden ?? 0);
      if (imgFile) fd.append('ImagenFile', imgFile);
      if (opcion?.id_opcion) await OpcionesProductoService.updateOpcion(opcion.id_opcion, fd);
      else await OpcionesProductoService.createOpcion(fd);
      onSaved();
    } catch { setError('Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl items-center">
      <div onClick={() => imgRef.current.click()}
        className="w-10 h-10 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer overflow-hidden bg-white flex-shrink-0 hover:border-indigo-400 transition-colors">
        {imgPrev
          ? <img src={imgPrev} alt="" className="w-full h-full object-cover" />
          : <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
      </div>
      <input ref={imgRef} type="file" accept="image/*" className="hidden"
        onChange={e => {
          const f = e.target.files[0]; if (!f) return;
          const r = new FileReader(); r.onload = ev => setImgPrev(ev.target.result); r.readAsDataURL(f);
          setImgFile(f);
        }} />
      <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre de la opción..."
        className={`${inputCls} flex-1 min-w-[140px]`} />
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-xs text-gray-500">+S/</span>
        <input type="number" min="0" step="0.50" value={precio} onChange={e => setPrecio(parseFloat(e.target.value) || 0)}
          className={`${inputCls} w-20 text-center`} />
      </div>
      <button onClick={guardar} disabled={saving} className={btnPrimary}>
        {saving ? '...' : <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Guardar</>}
      </button>
      <button onClick={onCancel} className={btnSecondary}>Cancelar</button>
      {error && <p className="w-full text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ── GrupoCard ─────────────────────────────────────────────────────────────────
function GrupoCard({ grupo, onRecargar, onDeleteGrupo }) {
  const [expanded,       setExpanded]       = useState(true);
  const [editando,       setEditando]       = useState(false);
  const [opciones,       setOpciones]       = useState(grupo.opciones ?? []);
  const [showOpcionForm, setShowOpcionForm] = useState(false);
  const [opcionEdit,     setOpcionEdit]     = useState(null);
  const [saving,         setSaving]         = useState(false);
  // ✅ Confirmación inline para eliminar opción (reemplaza window.confirm)
  const [confirmEliminar, setConfirmEliminar] = useState(null) // id_opcion o null

  const [nombre,      setNombre]      = useState(grupo.nombre);
  const [tipo,        setTipo]        = useState(grupo.tipo);
  const [obligatorio, setObligatorio] = useState(grupo.obligatorio);
  const [minSel,      setMinSel]      = useState(grupo.min_selecciones ?? 0);
  const [maxSel,      setMaxSel]      = useState(grupo.max_selecciones ?? '');
  const [estado,      setEstado]      = useState(grupo.estado ?? 'ACTIVO');

  const guardarGrupo = async () => {
    if (!nombre.trim()) return;
    setSaving(true);
    try {
      await OpcionesProductoService.updateGrupo(grupo.id_grupo, {
        id_producto: grupo.id_producto, nombre: nombre.trim(), tipo, obligatorio, estado,
        min_selecciones: parseInt(minSel) || 0,
        max_selecciones: maxSel === '' ? null : parseInt(maxSel),
        orden: grupo.orden,
      });
      setEditando(false); onRecargar();
    } catch { alert('Error al actualizar grupo'); }
    finally { setSaving(false); }
  };

  // ✅ Eliminar opción con confirmación inline
  const eliminarOpcion = async (idOpcion) => {
    try {
      await OpcionesProductoService.deleteOpcion(idOpcion);
      setOpciones(prev => prev.filter(o => o.id_opcion !== idOpcion));
    } catch { alert('Error al eliminar'); }
    finally { setConfirmEliminar(null); }
  };

  const toggleDisp = async (opcion) => {
    try {
      await OpcionesProductoService.toggleDisponibilidad(opcion.id_opcion);
      setOpciones(prev => prev.map(o => o.id_opcion === opcion.id_opcion ? { ...o, disponible: !o.disponible } : o));
    } catch { alert('Error al cambiar disponibilidad'); }
  };

  const recargarOpciones = async () => {
    try {
      const lista = await OpcionesProductoService.getOpcionesByGrupo(grupo.id_grupo);
      setOpciones(lista);
    } catch {}
    setShowOpcionForm(false); setOpcionEdit(null);
  };

  const tipoColor   = tipo === 'RADIO' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700';
  const estadoColor = estado === 'ACTIVO' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 cursor-pointer flex-wrap"
        onClick={() => !editando && setExpanded(v => !v)}>
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>

        {editando ? (
          <div className="flex flex-wrap gap-2 flex-1 items-center" onClick={e => e.stopPropagation()}>
            <input value={nombre} onChange={e => setNombre(e.target.value)} className={`${inputCls} flex-1 min-w-[120px]`} />
            <select value={tipo} onChange={e => setTipo(e.target.value)} className={`${selectCls} w-36`}>
              <option value="RADIO">RADIO (1 opción)</option>
              <option value="CHECKBOX">CHECKBOX (varias)</option>
            </select>
            <select value={estado} onChange={e => setEstado(e.target.value)} className={`${selectCls} w-28`}>
              <option value="ACTIVO">ACTIVO</option>
              <option value="INACTIVO">INACTIVO</option>
            </select>
            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer flex-shrink-0">
              <input type="checkbox" checked={obligatorio} onChange={e => setObligatorio(e.target.checked)} /> Obligatorio
            </label>
            <div className="flex items-center gap-1 flex-shrink-0">
              <input type="number" min="0" value={minSel} onChange={e => setMinSel(e.target.value)}
                placeholder="Mín" className={`${inputCls} w-16 text-center`} />
              <span className="text-gray-400 text-xs">—</span>
              <input type="number" min="1" value={maxSel} onChange={e => setMaxSel(e.target.value)}
                placeholder="Máx" className={`${inputCls} w-16 text-center`} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 flex-wrap min-w-0">
            <span className="font-semibold text-gray-900 text-sm">{grupo.nombre}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tipoColor}`}>{tipo === 'RADIO' ? 'Una opción' : 'Varias'}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${estadoColor}`}>{estado}</span>
            {obligatorio && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Requerido</span>}
            {(minSel > 0 || maxSel !== '') && (
              <span className="text-xs text-gray-500">
                {minSel > 0 && `Mín ${minSel}`}{minSel > 0 && maxSel !== '' && ' · '}{maxSel !== '' && `Máx ${maxSel}`}
              </span>
            )}
            <span className="text-xs text-gray-400 ml-auto">{opciones.length} opción{opciones.length !== 1 ? 'es' : ''}</span>
          </div>
        )}

        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {editando ? (
            <>
              <button onClick={guardarGrupo} disabled={saving} className={btnPrimary}>
                {saving ? '...' : 'Guardar'}
              </button>
              <button onClick={() => setEditando(false)} className={btnSecondary}>Cancelar</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditando(true)}
                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Editar">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
              <button onClick={() => onDeleteGrupo(grupo.id_grupo)}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
              <button onClick={() => setExpanded(v => !v)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Opciones */}
      {expanded && (
        <div className="p-3 flex flex-col gap-2">
          {opciones.map(opcion => (
            opcionEdit?.id_opcion === opcion.id_opcion ? (
              <OpcionForm key={opcion.id_opcion} idGrupo={grupo.id_grupo} opcion={opcion}
                onSaved={recargarOpciones} onCancel={() => setOpcionEdit(null)} />
            ) : (
              <div key={opcion.id_opcion} className="flex flex-col gap-1">
                <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg">
                  {opcion.imagen && (
                    <img src={`data:image/jpeg;base64,${opcion.imagen}`} alt=""
                      className="w-8 h-8 rounded object-cover flex-shrink-0" />
                  )}
                  <span className="flex-1 text-sm font-medium text-gray-800">{opcion.nombre}</span>
                  <span className={`text-xs font-bold flex-shrink-0 ${opcion.precio_adicional > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {opcion.precio_adicional > 0 ? `+${fmt(opcion.precio_adicional)}` : 'Incluido'}
                  </span>
                  <button onClick={() => toggleDisp(opcion)} title={opcion.disponible ? 'Deshabilitar' : 'Habilitar'}
                    className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${opcion.disponible ? 'text-green-500 hover:bg-green-50' : 'text-gray-300 hover:bg-gray-100'}`}>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      {opcion.disponible
                        ? <path d="M17 7H7C4.24 7 2 9.24 2 12s2.24 5 5 5h10c2.76 0 5-2.24 5-5s-2.24-5-5-5zm0 8c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
                        : <path d="M7 7C4.24 7 2 9.24 2 12s2.24 5 5 5h10c2.76 0 5-2.24 5-5s-2.24-5-5-5H7zm0 8c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>}
                    </svg>
                  </button>
                  <button onClick={() => setOpcionEdit(opcion)}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex-shrink-0">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                  {/* ✅ Botón eliminar — muestra confirm inline en vez de window.confirm */}
                  <button onClick={() => setConfirmEliminar(opcion.id_opcion)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
                {/* ✅ Confirm inline — aparece debajo de la opción */}
                {confirmEliminar === opcion.id_opcion && (
                  <ConfirmInline
                    mensaje={`¿Eliminar "${opcion.nombre}"?`}
                    onConfirm={() => eliminarOpcion(opcion.id_opcion)}
                    onCancel={() => setConfirmEliminar(null)}
                  />
                )}
              </div>
            )
          ))}

          {showOpcionForm ? (
            <OpcionForm idGrupo={grupo.id_grupo} opcion={null}
              onSaved={recargarOpciones} onCancel={() => setShowOpcionForm(false)} />
          ) : (
            <button onClick={() => setShowOpcionForm(true)}
              className="flex items-center justify-center gap-2 w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Agregar opción
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Modal principal ───────────────────────────────────────────────────────────
export default function ProductoOpcionesModal({ producto, onClose }) {
  const [grupos,        setGrupos]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showGrupoForm, setShowGrupoForm] = useState(false);
  const [guardando,     setGuardando]     = useState(false);
  // ✅ Confirmación inline para eliminar grupo
  const [confirmGrupo,  setConfirmGrupo]  = useState(null) // id_grupo o null

  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTipo,   setNuevoTipo]   = useState('CHECKBOX');
  const [nuevoOblig,  setNuevoOblig]  = useState(false);
  const [nuevoMin,    setNuevoMin]    = useState(0);
  const [nuevoMax,    setNuevoMax]    = useState('');

  const cargarGrupos = async () => {
    setLoading(true);
    try {
      const lista = await OpcionesProductoService.getGruposByProducto(producto.Id_producto ?? producto.id_producto);
      setGrupos(lista);
    } catch { alert('Error al cargar grupos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargarGrupos(); }, [producto.Id_producto ?? producto.id_producto]);

  const crearGrupo = async () => {
    if (!nuevoNombre.trim()) { alert('El nombre es requerido'); return; }
    setGuardando(true);
    try {
      await OpcionesProductoService.createGrupo({
        id_producto: producto.Id_producto ?? producto.id_producto,
        nombre: nuevoNombre.trim(), tipo: nuevoTipo, obligatorio: nuevoOblig,
        min_selecciones: parseInt(nuevoMin) || 0,
        max_selecciones: nuevoMax === '' ? null : parseInt(nuevoMax),
        orden: grupos.length, estado: 'ACTIVO',
      });
      setNuevoNombre(''); setNuevoTipo('CHECKBOX'); setNuevoOblig(false);
      setNuevoMin(0); setNuevoMax(''); setShowGrupoForm(false);
      cargarGrupos();
    } catch { alert('Error al crear grupo'); }
    finally { setGuardando(false); }
  };

  // ✅ Eliminar grupo — con confirm inline, sin window.confirm
  const eliminarGrupo = async (idGrupo) => {
    try { await OpcionesProductoService.deleteGrupo(idGrupo); cargarGrupos(); }
    catch { alert('Error al eliminar grupo'); }
    finally { setConfirmGrupo(null); }
  };

  const modal = (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 overflow-y-auto flex justify-center py-10 px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl h-fit">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Opciones del producto</h2>
            <p className="text-sm text-gray-500 mt-0.5">{producto.Nombre_producto ?? producto.nombre_producto}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col gap-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {grupos.length === 0 && !showGrupoForm && (
                <div className="text-center py-10 text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  <p className="font-medium text-gray-500">Sin opciones configuradas</p>
                  <p className="text-sm mt-1">Agrega un grupo para empezar (ej: "Elige tu salsa")</p>
                </div>
              )}

              {grupos.map(g => (
                <div key={g.id_grupo} className="flex flex-col gap-1">
                  <GrupoCard grupo={g} onRecargar={cargarGrupos}
                    // ✅ onDeleteGrupo muestra confirm inline en vez de ejecutar directo
                    onDeleteGrupo={(id) => setConfirmGrupo(id)} />
                  {confirmGrupo === g.id_grupo && (
                    <ConfirmInline
                      mensaje={`¿Eliminar el grupo "${g.nombre}" y todas sus opciones?`}
                      onConfirm={() => eliminarGrupo(g.id_grupo)}
                      onCancel={() => setConfirmGrupo(null)}
                    />
                  )}
                </div>
              ))}

              {showGrupoForm ? (
                <div className="border-2 border-indigo-200 rounded-xl p-4 bg-indigo-50">
                  <p className="font-semibold text-indigo-800 mb-3 text-sm">Nuevo grupo de opciones</p>
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1">Nombre del grupo *</label>
                      <input value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)}
                        placeholder="Ej: Elige tu salsa, Tamaño, Extras..."
                        className={inputCls} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1">Tipo *</label>
                        <select value={nuevoTipo} onChange={e => setNuevoTipo(e.target.value)} className={selectCls}>
                          <option value="CHECKBOX">CHECKBOX — varias opciones</option>
                          <option value="RADIO">RADIO — solo una opción</option>
                        </select>
                      </div>
                      <div className="flex items-center pt-5">
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input type="checkbox" checked={nuevoOblig} onChange={e => setNuevoOblig(e.target.checked)} className="w-4 h-4" />
                          Obligatorio
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1">Mínimo selecciones</label>
                        <input type="number" min="0" value={nuevoMin} onChange={e => setNuevoMin(e.target.value)}
                          placeholder="0" className={inputCls} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1">Máximo (vacío = sin límite)</label>
                        <input type="number" min="1" value={nuevoMax} onChange={e => setNuevoMax(e.target.value)}
                          placeholder="Sin límite" className={inputCls} />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => setShowGrupoForm(false)} className={btnSecondary}>Cancelar</button>
                      <button onClick={crearGrupo} disabled={guardando} className={btnPrimary}>
                        {guardando ? 'Creando...' : <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          Crear grupo
                        </>}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowGrupoForm(true)}
                  className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Agregar grupo de opciones
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}